import type { ClientMessage, ServerMessage } from '@shooting-range/shared';

type Listener = (message: ServerMessage) => void;
type StatusListener = (status: 'connecting' | 'online' | 'offline' | 'replaced') => void;

export class SquadConnection {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectTimer = 0;
  private attempts = 0;
  private manuallyClosed = false;
  private openedOnce = false;

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    this.manuallyClosed = false;
    this.emitStatus('connecting');
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.socket = new WebSocket(`${protocol}//${location.host}/ws`);
    this.socket.addEventListener('open', () => {
      this.attempts = 0;
      this.openedOnce = true;
      this.emitStatus('online');
      const token = sessionStorage.getItem('deadlight-session');
      if (token) this.send({ type: 'resumeSession', token });
    });
    this.socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(String(event.data)) as ServerMessage;
        for (const listener of this.listeners) listener(message);
      } catch {
        // Ignore malformed server frames; a healthy snapshot will follow.
      }
    });
    this.socket.addEventListener('close', (event) => {
      this.socket = null;
      if (event.code === 4001) {
        this.clearSession();
        this.emitStatus('replaced');
      } else this.emitStatus('offline');
      if (!this.manuallyClosed) this.scheduleReconnect();
    });
    this.socket.addEventListener('error', () => this.socket?.close());
  }

  send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  clearSession(): void {
    sessionStorage.removeItem('deadlight-session');
  }

  private scheduleReconnect(): void {
    window.clearTimeout(this.reconnectTimer);
    const delay = Math.min(10_000, 450 * 2 ** Math.min(this.attempts, 5)) + Math.random() * 300;
    this.attempts += 1;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private emitStatus(status: 'connecting' | 'online' | 'offline' | 'replaced'): void {
    for (const listener of this.statusListeners) listener(status);
    if (status === 'offline' && !this.openedOnce) this.emitStatus('connecting');
  }
}
